import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import StripePaymentForm from './StripePaymentForm';

const SERVICE_FEE = 1.00; // $1.00 service fee for all transactions

// Tiered pricing structure
const PARKING_RATES = {
  1: 4.00,
  2: 4.25,
  3: 4.75,
  4: 5.50,
  8: 8.75
};

const SimpleParkingForm = ({ spotId }) => {
  const [formData, setFormData] = useState({
    name: '',
    licensePlate: '',
    duration: 1, // In hours
  });
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [baseAmount, setBaseAmount] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateInput = () => {
    // Name validation
    if (formData.name.length < 2 || formData.name.length > 50) {
      alert('Name must be between 2-50 characters');
      return false;
    }

    // License plate validation (basic)
    const licensePlateRegex = /^[A-Z0-9-]{2,10}$/i;
    if (!licensePlateRegex.test(formData.licensePlate)) {
      alert('Invalid license plate format');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent rapid duplicate submissions (3 second cooldown)
    const now = Date.now();
    if (now - lastSubmitTime < 3000) {
      alert('Please wait a moment before submitting again.');
      return;
    }

    setSubmitting(true);
    setLastSubmitTime(now);

    // Validate input
    if (!validateInput()) {
      setSubmitting(false);
      return;
    }

    try {
      // Calculate base amount using tiered pricing
      const hours = parseFloat(formData.duration);
      const parkingCost = PARKING_RATES[hours] || 3.75; // Default to $3.75 if duration not found
      setBaseAmount(parkingCost);

      // Calculate total with service fee
      const totalAmount = parkingCost + SERVICE_FEE;
      setCalculatedAmount(totalAmount);

      // Show payment form
      setShowPayment(true);
    } catch (error) {
      console.error('Error saving booking:', error);
      if (error.code === 'unavailable') {
        alert('No internet connection. Please try again.');
      } else if (error.code === 'permission-denied') {
        alert('Access denied. Please contact support.');
      } else {
        alert('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = (paymentResult) => {
    setBookingId(paymentResult.bookingId);
    setSubmitted(true);
    setShowPayment(false);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setSubmitting(false);
  };

  // Payment screen
  if (showPayment) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ maxWidth: '560px', width: '100%', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', padding: '32px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '8px', margin: '0 0 8px 0' }}>
              Complete Payment
            </h2>
            <p style={{ color: '#6b7280', margin: 0 }}>
              Please enter your payment information to complete the booking
            </p>
          </div>

          <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#6b7280' }}>Name:</span>
              <span style={{ fontWeight: '600', color: '#111827' }}>{formData.name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#6b7280' }}>License Plate:</span>
              <span style={{ fontWeight: '600', color: '#111827' }}>{formData.licensePlate}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#6b7280' }}>Duration:</span>
              <span style={{ fontWeight: '600', color: '#111827' }}>{formData.duration} hour{formData.duration > 1 ? 's' : ''}</span>
            </div>
            {spotId && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Spot:</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{spotId}</span>
              </div>
            )}
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '12px', paddingTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Parking Fee:</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>${baseAmount.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Service Fee:</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>${SERVICE_FEE.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #e5e7eb', marginTop: '8px', paddingTop: '12px' }}>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>Total:</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#059669' }}>${calculatedAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <StripePaymentForm
            amount={calculatedAmount}
            baseAmount={baseAmount}
            serviceFee={SERVICE_FEE}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            bookingData={{
              name: formData.name,
              licensePlate: formData.licensePlate,
              duration: `${formData.duration} hour${formData.duration > 1 ? 's' : ''}`,
              hours: parseFloat(formData.duration),
              spotId: spotId || 'General',
            }}
          />

          <button
            onClick={() => {
              setShowPayment(false);
              setSubmitting(false);
            }}
            style={{
              width: '100%',
              marginTop: '16px',
              padding: '12px 16px',
              borderRadius: '8px',
              fontWeight: '600',
              border: '1px solid #d1d5db',
              cursor: 'pointer',
              background: 'white',
              color: '#374151',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.background = 'white'}
          >
            Back to Form
          </button>
        </div>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ maxWidth: '448px', width: '100%', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', padding: '32px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '64px', width: '64px', borderRadius: '50%', backgroundColor: '#d1fae5', marginBottom: '16px' }}>
              <svg style={{ height: '40px', width: '40px', color: '#059669' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" width="40" height="40">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '8px', margin: '0 0 8px 0' }}>Payment Successful!</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', margin: '0 0 24px 0' }}>Your parking spot has been reserved</p>

            <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', marginBottom: '24px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {spotId && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ color: '#6b7280' }}>Spot:</span>
                  <span style={{ fontWeight: '600', color: '#111827' }}>{spotId}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ color: '#6b7280' }}>Name:</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{formData.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ color: '#6b7280' }}>License Plate:</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{formData.licensePlate}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ color: '#6b7280' }}>Duration:</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{formData.duration} hour{formData.duration > 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Booking ID:</span>
                <span style={{ fontFamily: 'monospace', fontSize: '14px', color: '#111827' }}>{bookingId}</span>
              </div>
            </div>

            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: '', licensePlate: '', duration: 1 });
                setBookingId('');
                setLastSubmitTime(0);
                setShowPayment(false);
                setCalculatedAmount(0);
                setBaseAmount(0);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                background: '#1e3a5f',
                color: 'white',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#2d5a7b'}
              onMouseLeave={(e) => e.target.style.background = '#1e3a5f'}
            >
              Book Another Spot
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form screen
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a7b 100%)', padding: '60px 20px', marginBottom: '40px' }}>
        <div style={{ maxWidth: '672px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '600', color: 'white', marginBottom: '8px', margin: '0 0 8px 0' }}>
            {spotId ? `Parking at Spot ${spotId}` : 'Parking Registration'}
          </h1>
          <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.9)', margin: 0 }}>Complete the form to reserve your parking spot</p>
        </div>
      </div>

      {/* Form */}
      <div style={{ maxWidth: '672px', margin: '0 auto', padding: '0 16px' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', padding: '32px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Name */}
            <div>
              <label htmlFor="name" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Your Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                style={{ width: '100%', padding: '12px 16px', fontSize: '16px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }}
                placeholder="Enter your name"
              />
            </div>

            {/* License Plate */}
            <div>
              <label htmlFor="licensePlate" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                License Plate Number *
              </label>
              <input
                type="text"
                id="licensePlate"
                name="licensePlate"
                required
                value={formData.licensePlate}
                onChange={handleChange}
                style={{ width: '100%', padding: '12px 16px', fontSize: '16px', border: '1px solid #d1d5db', borderRadius: '8px', textTransform: 'uppercase', boxSizing: 'border-box', outline: 'none' }}
                placeholder="ABC-1234"
              />
            </div>

            {/* Duration - Bubble Selectors */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                How long do you want to park? *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                {Object.entries(PARKING_RATES).map(([hours, price]) => {
                  const isSelected = formData.duration === parseFloat(hours);
                  const totalWithFee = price + SERVICE_FEE;
                  return (
                    <div
                      key={hours}
                      onClick={() => setFormData(prev => ({ ...prev, duration: parseFloat(hours) }))}
                      style={{
                        padding: '16px',
                        border: isSelected ? '2px solid #1e3a5f' : '2px solid #d1d5db',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#f0f4f8' : 'white',
                        transition: 'all 0.2s ease',
                        textAlign: 'center',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#9ca3af';
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#1e3a5f',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg style={{ width: '12px', height: '12px', color: 'white' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>
                        {hours} hour{hours > 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#059669', marginBottom: '2px' }}>
                        ${price.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        +${SERVICE_FEE.toFixed(2)} fee
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                        Total: ${totalWithFee.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '16px 24px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '18px',
                border: 'none',
                cursor: submitting ? 'not-allowed' : 'pointer',
                background: submitting ? '#9ca3af' : '#1e3a5f',
                color: 'white',
                transition: 'all 0.2s ease',
                boxShadow: submitting ? 'none' : '0 4px 6px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (!submitting) e.target.style.background = '#2d5a7b';
              }}
              onMouseLeave={(e) => {
                if (!submitting) e.target.style.background = '#1e3a5f';
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" width="20" height="20">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Continue to Payment'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SimpleParkingForm;
